import { authApiService } from './authService';

// Helper to extract data from the response structure
const extractResponseData = (response) => {
  if (response.data && response.data.statusCode !== undefined) {
    return {
      success: response.data.statusCode,
      data: response.data.body?.data || response.data.body,
      message: response.data.body?.message || ''
    };
  }
  // Fallback for different response structures
  return {
    success: response.data?.success || response.success || false,
    data: response.data?.data || response.data,
    message: response.data?.message || response.message || ''
  };
};

export const aiService = {
  transcribeAudio: async (file, sessionId) => {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('sessionId', sessionId);
    // Long timeout: transcription + vector store can take several minutes for long audio
    const TRANSCRIBE_REQUEST_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
    return authApiService.post('/ai/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: TRANSCRIBE_REQUEST_TIMEOUT_MS,
    });
  },

  /** Start transcription (async). Returns { jobId } on 202; result is delivered via WebSocket (transcription_complete / transcription_error). */
  transcribeAudioAsync: async (file, sessionId) => {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('sessionId', sessionId);
    const TRANSCRIBE_REQUEST_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours for upload + 202 (long recordings)
    const response = await authApiService.post('/ai/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: TRANSCRIBE_REQUEST_TIMEOUT_MS,
      validateStatus: (status) => status === 202 || status === 200,
    });
    if (response.status === 202) {
      const jobId = response.data?.body?.data?.jobId ?? response.data?.data?.jobId;
      if (!jobId) throw new Error('Transcription started but no job ID returned');
      return { jobId };
    }
    if (response.status === 200) {
      return { sync: true, data: response.data };
    }
    throw new Error(response.data?.body?.message || 'Transcription failed');
  },

  /** Start transcription + auto-generate SOAP (async). Returns { jobId } on 202; final result via WebSocket. */
  transcribeAudioWithSoapAsync: async (file, sessionId, options = {}) => {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('sessionId', sessionId);
    // Higher timeout: transcription + vector store + SOAP generation
    const TRANSCRIBE_WITH_SOAP_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours

    const response = await authApiService.post('/ai/transcribe-with-soap', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: TRANSCRIBE_WITH_SOAP_TIMEOUT_MS,
      validateStatus: (status) => status === 202 || status === 200,
      // Real byte-level upload progress ({ loaded, total }) so a slow/stuck upload is visible.
      ...(typeof options?.onUploadProgress === 'function'
        ? { onUploadProgress: options.onUploadProgress }
        : {}),
    });

    if (response.status === 202) {
      const data = response.data?.body?.data ?? response.data?.data ?? {};
      const jobId = data.jobId;
      if (!jobId) throw new Error('Transcription (with SOAP) started but no job ID returned');
      return {
        jobId,
        status: data.status,
        queuePosition: data.queuePosition,
      };
    }

    if (response.status === 200) {
      return { sync: true, data: response.data };
    }

    throw new Error(response.data?.body?.message || 'Transcription with SOAP failed');
  },

  /** Transcribe audio already in S3 for this session and auto-generate SOAP. */
  transcribeExistingSession: async (sessionId) => {
    const TRANSCRIBE_EXISTING_TIMEOUT_MS = 2 * 60 * 60 * 1000;
    const response = await authApiService.post(
      '/ai/transcribe-existing',
      { sessionId },
      {
        timeout: TRANSCRIBE_EXISTING_TIMEOUT_MS,
        validateStatus: (status) => status === 202 || status === 200,
      }
    );
    if (response.status === 202 || response.status === 200) {
      const data = response.data?.body?.data ?? response.data?.data ?? {};
      const jobId = data.jobId ?? response.data?.body?.data?.jobId ?? response.data?.data?.jobId;
      if (!jobId && response.status === 202) {
        throw new Error('Transcription started but no job ID returned');
      }
      return {
        jobId,
        status: data.status,
        queuePosition: data.queuePosition,
        data: response.data,
      };
    }
    throw new Error(response.data?.body?.message || 'Transcription from saved audio failed');
  },

  getTranscriptionJobs: async (params = {}) => {
    return authApiService.get('/ai/transcription-jobs', { params });
  },

  getTranscriptionJobById: async (jobId) => {
    return authApiService.get(`/ai/transcription-jobs/${jobId}`);
  },

  generateSoapNote: async (transcriptionText, transcriptionId, patientContext = {}, sessionId, options = {}) => {
    const SOAP_GEN_TIMEOUT_MS = 45 * 60 * 1000;
    const body = {
      transcriptionText,
      transcriptionId,
      patientContext,
      sessionId,
    };
    // Optional per-chart HPI depth override (1=Concise, 2=Balanced, 3=Detailed).
    if (options && options.hpiDepthLevel != null) {
      body.hpiDepthLevel = options.hpiDepthLevel;
    }
    return authApiService.post(
      '/ai/generate-soap',
      body,
      { timeout: SOAP_GEN_TIMEOUT_MS }
    );
  },

  /** Queue SOAP from a live transcript (async). Returns { jobId } on 202; result via WebSocket. */
  generateSoapNoteAsync: async (transcriptionText, transcriptionId, patientContext = {}, sessionId, options = {}) => {
    const SOAP_ASYNC_TIMEOUT_MS = 2 * 60 * 1000;
    const body = {
      transcriptionText,
      transcriptionId,
      patientContext,
      sessionId,
    };
    if (options && options.hpiDepthLevel != null) {
      body.hpiDepthLevel = options.hpiDepthLevel;
    }
    const response = await authApiService.post('/ai/generate-soap-async', body, {
      timeout: SOAP_ASYNC_TIMEOUT_MS,
      validateStatus: (status) => status === 202 || status === 200,
    });
    if (response.status === 202 || response.status === 200) {
      const data = response.data?.body?.data ?? response.data?.data ?? {};
      const jobId = data.jobId;
      if (!jobId && response.status === 202) {
        throw new Error('SOAP generation started but no job ID returned');
      }
      return {
        jobId,
        status: data.status,
        queuePosition: data.queuePosition,
        transcriptionId: data.transcriptionId,
        data: response.data,
      };
    }
    throw new Error(response.data?.body?.message || 'SOAP generation failed');
  },

  /** After live transcribe stop: Azure Translator scans for Spanish segments and translates to English. */
  translateLiveTranscript: async (text) => {
    return authApiService.post('/ai/translate-live-transcript', { text })
  },

  analyzeText: async (text, patientContext = {}) => {
    return authApiService.post('/ai/analyze', {
      text,
      patientContext
    });
  },

  getSOAPNote: async (soapNoteId) => {
    return authApiService.get(`/ai/soap-notes/${soapNoteId}`);
  },

  updateSOAPNote: async (soapNoteId, data) => {
    return authApiService.patch(`/ai/soap-notes/${soapNoteId}`, data);
  },

  /** Regenerate ONLY the HPI/Subjective at a depth level (1=Concise, 2=Balanced, 3=Detailed). Cached per level server-side. */
  regenerateHpi: async (soapNoteId, hpiDepthLevel) => {
    const HPI_REGEN_TIMEOUT_MS = 10 * 60 * 1000;
    return authApiService.post(
      `/ai/soap-notes/${soapNoteId}/regenerate-hpi`,
      { hpiDepthLevel },
      { timeout: HPI_REGEN_TIMEOUT_MS }
    );
  },

  /**
   * Regenerate full SOAP note with a template instance. Cached per template for the session.
   * @param {string} soapNoteId
   * @param {string} [templateInstanceId] - defaults to user's active template on the server
   */
  regenerateWithTemplate: async (soapNoteId, templateInstanceId) => {
    const TEMPLATE_REGEN_TIMEOUT_MS = 45 * 60 * 1000;
    const body = {};
    if (templateInstanceId) {
      body.templateInstanceId = templateInstanceId;
    }
    return authApiService.post(
      `/ai/soap-notes/${soapNoteId}/regenerate-with-template`,
      body,
      { timeout: TEMPLATE_REGEN_TIMEOUT_MS }
    );
  },

  getUserSOAPNotes: async (sessionId) => {
    return authApiService.get(`/ai/users/${sessionId}/soap-notes`);
  },

  healthCheck: async () => {
    return authApiService.get('/ai/health');
  },

  testTranscription: async () => {
    return authApiService.get('/ai/test/transcription');
  },
  correctSoapNote: async (soapNoteId, userPrompt) => {
    return authApiService.post(`/ai/soap-notes/${soapNoteId}/magic-editor`, { userPrompt });
  },

  /** Rewrite chart pronouns via magic-editor pipeline + ScribeAI master prompt */
  correctSoapNotePronouns: async (soapNoteId, pronouns) => {
    const PRONOUN_CORRECTION_TIMEOUT_MS = 5 * 60 * 1000;
    return authApiService.post(
      `/ai/soap-notes/${soapNoteId}/magic-editor`,
      { correctionType: "pronouns", pronouns },
      { timeout: PRONOUN_CORRECTION_TIMEOUT_MS }
    );
  },

  scanUnknownMedications: async (soapNoteId) => {
    const response = await authApiService.post(
      `/ai/soap-notes/${soapNoteId}/unknown-medications/scan`,
      {}
    );
    return response?.data?.body?.data || null;
  },

  // "Keep & ignore": permanently stop flagging this name on the SOAP note
  ignoreUnknownMedication: async (soapNoteId, originalText) => {
    const response = await authApiService.post(
      `/ai/soap-notes/${soapNoteId}/unknown-medications/ignore`,
      { originalText }
    );
    return response?.data?.body?.data || null;
  },

  applyUnknownMedication: async (soapNoteId, payload) => {
    const response = await authApiService.post(
      `/ai/soap-notes/${soapNoteId}/unknown-medications/apply`,
      payload
    );
    return response?.data?.body?.data || null;
  },
 // Scribe submits SOAP note for review
  submitSOAPNoteForReview: async (soapNoteId, data = {}) => {
    const response = await authApiService.patch(`/admin/soap-notes/${soapNoteId}/submit-review`, data);
    return extractResponseData(response);
  },

  // Admin approves SOAP note
  approveSOAPNote: async (soapNoteId, data = {}) => {
    const response = await authApiService.patch(`/admin/soap-notes/${soapNoteId}/approve`, data);
    return extractResponseData(response);
  },

  // Admin requests revisions
  requestSOAPNoteRevisions: async (soapNoteId, data = {}) => {
    const response = await authApiService.patch(`/admin/soap-notes/${soapNoteId}/request-revisions`, data);
    return extractResponseData(response);
  },
};